import { HttpApiEndpoint, HttpApiGroup, HttpApiSchema } from "@effect/platform";
import * as S from "effect/Schema";
import { NotFound } from "./folders.js";
import { Forbidden } from "./shares.js";
import { PrincipalMiddleware } from "./principal.js";
import { ContractType } from "./admin-clients.js";
import { LanguageSchema } from "./column-schemas.js";
/**
 * A comma-joined contract-type list on the wire, decoded to an array — the
 * same encoding `employees.ts`'s `UuidCsvList` uses for url-param
 * collections, since `HttpApiEndpoint` url params must encode to strings.
 */
const ContractTypeCsvList = S.transform(S.String, S.Array(ContractType), {
    strict: true,
    decode: (s) => s
        .split(",")
        .map((t) => t.trim())
        .filter((value) => value === "pima" ||
        value === "argumed" ||
        value === "dearest" ||
        value === "zap"),
    encode: (types) => types.join(","),
});
export const DownloadFileParams = S.Struct({
    id: S.UUID,
});
/**
 * The authorized file's bytes are unreachable: either the `yp_files` row
 * carries no `filename_disk` (it never got an object key, and no placeholder
 * backfill can give it one — there is no path to write to), or the stored
 * object itself cannot be read. One tag for both, because the caller can do
 * nothing different about them.
 *
 * Distinct from `NotFound`, which this group uses for an absent folder or
 * record. It confirms nothing about existence: a nonexistent or unshared id
 * is still `Forbidden`, so this only ever reaches a caller already
 * authorized to read the file.
 */
export const FileUnavailable = S.Struct({
    _tag: S.Literal("FileUnavailable"),
});
export const FileDownloadInfo = S.Struct({
    filename: S.NonEmptyString,
    contentType: S.NonEmptyString,
});
/**
 * A `yp_files` row addressed by `uploadFile`/`deleteFile` — the fields the
 * documents/folders screens need to render a listing entry without a
 * follow-up read. `filesize` is the byte length the storage write recorded,
 * not a caller-supplied value.
 */
export const FileRow = S.Struct({
    id: S.UUID,
    filename_download: S.String,
    type: S.NullOr(S.String),
    folder: S.NullOr(S.UUID),
    client_id: S.NullOr(S.UUID),
    filesize: S.NullOr(S.String),
    translation_group_id: S.NullOr(S.Int),
});
/**
 * Upload metadata rides `urlParams` (the assets package's own
 * `uploadAssetFile` convention: the body IS the file bytes, never
 * multipart) — `folder_id` omitted uploads to the caller's client root.
 */
export const UploadFileParams = S.Struct({
    filename: S.NonEmptyString,
    content_type: S.NonEmptyString,
    folder_id: S.optional(S.UUID),
});
/**
 * Stores one file into a client space the caller may act in — the caller's
 * own client with no `folder_id`, or the client owning the target folder
 * otherwise, checked against `canManageClient` the same way `viewFolders`
 * authorizes a platform role acting outside its own client — and grants the
 * uploader an immediate `file_shares` row so `downloadFile`/`sharesForUser`
 * see it without a separate share step.
 */
export const UploadFile = HttpApiEndpoint.post("uploadFile", "/files/upload")
    .setUrlParams(UploadFileParams)
    .setPayload(HttpApiSchema.Uint8Array())
    .addSuccess(S.Struct({ data: FileRow }))
    .addError(Forbidden, { status: 403 });
export const UploadPublicFileParams = S.Struct({
    filename: S.NonEmptyString,
    content_type: S.NonEmptyString,
});
/** Public branding/profile media for authenticated users. The public scope is
 * explicit in the intent, so callers cannot smuggle an arbitrary folder id or
 * client ownership signal into the write. */
export const UploadPublicFile = HttpApiEndpoint.post("uploadPublicFile", "/files/upload-public")
    .setUrlParams(UploadPublicFileParams)
    .setPayload(HttpApiSchema.Uint8Array())
    .addSuccess(S.Struct({ data: FileRow }));
export const UploadDocumentFileParams = S.Struct({
    filename_download: S.NonEmptyString,
    content_type: S.NonEmptyString,
    folder_id: S.UUID,
    client_id: S.optional(S.UUID),
    title: S.NonEmptyString,
    translation_group_id: S.optional(S.NumberFromString.pipe(S.int())),
    language: LanguageSchema,
    description: S.optional(S.String),
    contract_types: S.optional(ContractTypeCsvList),
});
/** Metadata-rich document-library upload. Optional client ownership is a
 * closed scope: omitted means global internal library; present means exactly
 * that client, and the handler verifies both persona and folder ownership.
 * A client-owned upload also writes the owner `file_shares` row the company
 * library lists (`user_id` NULL), plus a self-share when the uploader is a
 * member of that client. */
export const UploadDocumentFile = HttpApiEndpoint.post("uploadDocumentFile", "/files/upload-document")
    .setUrlParams(UploadDocumentFileParams)
    .setPayload(HttpApiSchema.Uint8Array())
    .addSuccess(S.Struct({ data: FileRow }))
    .addError(Forbidden, { status: 403 });
export const FileTranslationVariantParams = S.Struct({
    translation_group_id: S.NumberFromString.pipe(S.int()),
    language: S.Literal("de-DE", "en-US"),
    exclude_file_id: S.optional(S.UUID),
});
export const FileTranslationVariantRow = S.Struct({
    id: S.UUID,
    filename_download: S.String,
    title: S.NullOr(S.String),
    description: S.NullOr(S.String),
    folder: S.NullOr(S.UUID),
    client_id: S.NullOr(S.UUID),
    uploaded_by: S.NullOr(S.UUID),
    visibility_scope: S.String,
    translation_group_id: S.Int,
    language: S.Literal("de-DE", "en-US"),
    metadata: S.Unknown,
});
/** Looks up one language sibling through the file-management authorization
 * boundary; callers never issue a generic collection query. */
export const GetFileTranslationVariant = HttpApiEndpoint.get("getFileTranslationVariant", "/files/translation-variant")
    .setUrlParams(FileTranslationVariantParams)
    .addSuccess(S.Struct({ data: S.NullOr(FileTranslationVariantRow) }))
    .addError(Forbidden, { status: 403 });
export const UpdateDocumentMetadataPayload = S.Struct({
    id: S.UUID,
    filename_download: S.NonEmptyString,
    title: S.NonEmptyString,
    folder_id: S.UUID,
    description: S.NullOr(S.String),
    contract_types: S.Array(ContractType),
});
/** Updates the selected document and propagates shared metadata to every
 * translation sibling in the same request transaction. */
export const UpdateDocumentMetadata = HttpApiEndpoint.post("updateDocumentMetadata", "/files/update-document-metadata")
    .setPayload(UpdateDocumentMetadataPayload)
    .addSuccess(S.Struct({
    data: S.Array(S.Struct({ id: S.UUID, translation_group_id: S.NullOr(S.Int) })),
}))
    .addError(Forbidden, { status: 403 });
export const DeleteFilePayload = S.Struct({
    id: S.UUID,
});
/**
 * Removes a file the caller may manage, its storage bytes, and every
 * `file_shares` row pointing at it. A denied and nonexistent id are the same
 * `NotFound`, so this intent never confirms that an inaccessible file exists.
 */
export const DeleteFile = HttpApiEndpoint.post("deleteFile", "/files/delete")
    .setPayload(DeleteFilePayload)
    .addSuccess(S.Struct({ data: S.Struct({ deleted: S.Literal(true) }) }))
    .addError(NotFound, { status: 404 });
/**
 * Raw file bytes for one `yp_files` row (`GET /assets/{id}` today) —
 * `application/octet-stream` regardless of the stored file's real type: the
 * caller already knows the filename/mimetype from wherever it found this id
 * (a folder listing, a course-metadata row, a share) and sets its own
 * `Content-Disposition`/blob type from that, not from this response.
 * Authorization is a `file_shares` row for the caller — the same rule
 * `shares.sharesForUser` reads for the same table; a nonexistent id and an
 * unshared one are indistinguishable (both Forbidden), so this never
 * confirms whether an id exists.
 */
export const DownloadFile = HttpApiEndpoint.get("downloadFile", "/files/download")
    .setUrlParams(DownloadFileParams)
    .addSuccess(HttpApiSchema.Uint8Array({ contentType: "application/octet-stream" }))
    .addError(Forbidden, { status: 403 })
    .addError(FileUnavailable, { status: 404 });
/**
 * Browser-facing metadata for the same authorized file as `DownloadFile`.
 * Keeping the filename and stored MIME type in this domain contract prevents
 * browser callers from guessing them from a database view or constructing an
 * untyped `application/octet-stream` Blob.
 */
export const GetFileDownloadInfo = HttpApiEndpoint.get("getFileDownloadInfo", "/files/download-info")
    .setUrlParams(DownloadFileParams)
    .addSuccess(S.Struct({ data: FileDownloadInfo }))
    .addError(Forbidden, { status: 403 })
    .addError(FileUnavailable, { status: 404 });
export const filesGroup = HttpApiGroup.make("files")
    .add(DownloadFile)
    .add(GetFileDownloadInfo)
    .add(UploadFile)
    .add(UploadPublicFile)
    .add(UploadDocumentFile)
    .add(GetFileTranslationVariant)
    .add(UpdateDocumentMetadata)
    .add(DeleteFile)
    .middleware(PrincipalMiddleware);
//# sourceMappingURL=files.js.map